import { toDate, toDateStr, toMoment } from "~/app/core/model/date-str";
import { NetworkConnectionType, NetworkService } from "~/app/core/network.service";
import { StorageService } from "~/app/core/storage.service";
import { LocationService } from "~/app/core/location.service";
import { NotificationService } from "~/app/core/notification.service";
import { SettingsService } from "~/app/core/settings.service";
import { OfferInfo } from "~/app/core/model/offer-info";
import { OfferSearch } from "~/app/core/model/offer-search";
import { SponsorInfo } from "~/app/core/model/sponsor-info";
import { AdInfo } from "~/app/core/model/ad-info";
import { InterfaceOfferService } from "~/app/core/offer-service/interface-offer-service";
import { SuccessResult } from "~/app/core/model/connect";
import { UserProfileService } from "~/app/core/user-profile-service/user-profile.service";
import { UserInfo } from "~/app/core/model/user-info";
import * as Log from "~/app/core/log";
import { Settings } from "~/app/core/model/settings";
import { RPCService } from "~/app/core/rpc.service";
import { OfferInfo as OfferInfoRPC } from "~/app/core/model/rpc/offer-info";
import { OfferInfoList } from "~/app/core/model/rpc/offer-info-list";
import { AlternateLocationsList } from "~/app/core/model/rpc/alternate-locations-list";
import { AdInfoRPC } from "~/app/core/model/rpc/ad-info";
import { parseAddress } from "~/app/core/util/string-util";
import { AddressInfo } from "~/app/core/model/address-info";
import { OfferAvailableInfo } from "~/app/core/model/rpc/offer-available-info";
import { OfferSearchResultLocation } from "~/app/core/model/offer-search-result-location";
import { OfferFilterOptions } from "~/app/core/model/offer-filter-options";
import { OfferItemView } from "~/app/components/offer-item/offer-item-view";

export class HttpOfferService implements InterfaceOfferService {
    constructor(
        private readonly networkService: NetworkService,
        private readonly userProfile: UserProfileService,
        private readonly locationService: LocationService,
        private readonly notificationService: NotificationService,
        private readonly settingsService: SettingsService,
        private readonly storageService: StorageService,
        private readonly rpcService: RPCService
    ) {
    }

    private get settings(): Settings {
        return this.settingsService.loadSettings();
    }

    initService() {
        const offerSearch = this.storageService.loadOfferSearch();
        if (!offerSearch) {
            this.storageService.storeOfferSearch({
                location: true,
                radius: 50
            });
        }
    }

    nearbyOffer(offerId: number, addressId: number) {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.nearbyDate) {
            offerInfo.nearbyDate = toDateStr(this.settings.calculateCurrentTime());
            // allow this call to be made later
            this.rpcService.OfferNearby_queue({ offer_id: offerId, address_id: addressId });
            this.storageService.storeOfferInfo(offerInfo);
        }
    }

    seenOffer(offerId: number, offsetMinutes: number = 0) {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        // only update the seen time if the old time is further in the future than the new time
        const nowSeenDate = this.settings.calculateCurrentTime();
        const newSeenDate = this.settings.calculateCurrentTime();
        newSeenDate.setTime(newSeenDate.getTime() + (offsetMinutes * 60 * 1000));
        if (OfferItemView.isOfferUnseenLately(nowSeenDate, offerInfo)) {
            // make sure it stays this way for at least 10 minutes
            if (!offerInfo.unseenUntilDate) {
                offerInfo.unseenUntilDate = toDateStr(newSeenDate);
            }
        } else {
            offerInfo.unseenUntilDate = null;
        }

        offerInfo.lastSeenDate = toDateStr(nowSeenDate);
        if (offerInfo && (!offerInfo.seenDate || toDate(offerInfo.seenDate).getTime() > newSeenDate.getTime())) {
            offerInfo.seenDate = toDateStr(newSeenDate);
        }
        this.storageService.storeOfferInfo(offerInfo);
    }

    watchedVideo(offerId: number): OfferInfo {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            offerInfo.watchedVideo = toDateStr(this.settings.calculateCurrentTime());
            // allow this call to be made later
            this.rpcService.OfferView_queue({ offer_id: offerId });
            this.storageService.storeOfferInfo(offerInfo);
        }
        return offerInfo;
    }

    generateOfferShare(offerId: number): Promise<{ url: string, image: string }> {
        return new Promise((resolve, reject) => {
            this.rpcService.OfferGenerateShareWithImage({ offer_id: offerId }).then((result) => {
                resolve(result);
            }, (error) => {
                reject(error);
            });
        });
    }

    revealOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.revealed) {
            this.rpcService.OfferReveal_queue({ offer_id: offerId });
            offerInfo.revealed = true;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    repeatUnlockOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (this.networkService.connectionType !== NetworkConnectionType.None) {
            if (offerInfo && !offerInfo.repeatUnlocked) {
                return new Promise((resolve, reject) => {
                    this.rpcService.OfferRepeatUnlock({ offer_id: offerId }).then(() => {
                        offerInfo.repeatUnlocked = true;
                        this.storageService.storeOfferInfo(offerInfo);
                        resolve(offerInfo);
                    }, (error) => {
                        reject(error);
                    });
                });
            }
        }
        return Promise.resolve(offerInfo);
    }

    favoriteOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && !offerInfo.favorite) {
            this.rpcService.OfferFavorite_queue({ offer_id: offerId });
            offerInfo.favorite = true;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    unfavoriteOffer(offerId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo && offerInfo.favorite) {
            this.rpcService.OfferUnfavorite_queue({ offer_id: offerId });
            offerInfo.favorite = false;
            this.storageService.storeOfferInfo(offerInfo);
        }
        return Promise.resolve(offerInfo);
    }

    redeemOffer(offerId: number, addressId: number): Promise<OfferInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            if (!offerInfo.redeemedDate) {
                const userInfo: UserInfo = this.userProfile.userInfo;
                if (userInfo && userInfo.authToken) {
                    if (this.settings.enforceRedeemNet || this.networkService.connectionType !== NetworkConnectionType.None) {
                        return new Promise((resolve, reject) => {
                            this.rpcService.OfferRedeem({ offer_id: offerId, address_id: addressId }).then((result: void) => {
                                offerInfo.redeemedDate = toDateStr(this.settings.calculateCurrentTime());
                                offerInfo.favorite = true;
                                if (offerInfo.bounceBackOfferId) {
                                    const when: Date = this.settings.calculateBounceBack(toDate(offerInfo.redeemedDate), offerInfo.sample);
                                    offerInfo.bouncebackDate = toDateStr(when);
                                }
                                this.storageService.storeOfferInfo(offerInfo);
                                resolve(offerInfo);
                            }, (error) => {
                                reject(error);
                            });
                        });
                    } else {
                        this.rpcService.OfferRedeem_queue({ offer_id: offerId, address_id: addressId });
                        offerInfo.redeemedDate = toDateStr(this.settings.calculateCurrentTime());
                        offerInfo.favorite = true;
                        if (offerInfo.bounceBackOfferId) {
                            const when: Date = this.settings.calculateBounceBack(toDate(offerInfo.redeemedDate), offerInfo.sample);
                            offerInfo.bouncebackDate = toDateStr(when);
                        }
                        this.storageService.storeOfferInfo(offerInfo);
                        return Promise.resolve(offerInfo);
                    }
                } else {
                    return Promise.reject("Not authenticated");
                }
            } else {
                return Promise.resolve(offerInfo);
            }
        } else {
            return Promise.reject("Missing");
        }
    }

    isAvailableOffer(offerId: number): Promise<OfferAvailableInfo> {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            if (!offerInfo.redeemedDate || offerInfo.nextAvailableDate) {
                if (this.networkService.connectionType === NetworkConnectionType.None) {
                    return Promise.resolve({ available: false });
                } else {
                    const userInfo: UserInfo = this.userProfile.userInfo;
                    if (userInfo && userInfo.authToken) {
                        return new Promise((resolve, reject) => {
                            this.rpcService.OfferIsAvailable({ offer_id: offerId }).then((result) => {
                                Log.debug(Log.Tag.HttpOfferService, result);
                                resolve(result);
                            }, (error) => {
                                resolve({ available: false });
                            });
                        });
                    } else {
                        return Promise.resolve({ available: false });
                    }
                }
            } else {
                return Promise.resolve({ available: false });
            }
        } else {
            return Promise.resolve({ available: false });
        }
    }

    testResetOffers(): Promise<SuccessResult> {
        return new Promise((resolve, reject) => {
            this.rpcService.ResetOffers().then((result) => {
                this.storageService.resetOffers();
                resolve({ success: true });
            }, (error) => {
                reject(error);
            });
        });
    }

    testResetOffer(offerId: number, resetRedeemed: boolean, resetRevealed: boolean, resetRepeatUnlocked: boolean, resetThumbsup: boolean, resetBounceback: boolean, resetVideo: boolean): Promise<OfferInfo> {
        const resetOffer = () => {
            const offerInfo = this.storageService.loadOfferInfo(offerId);
            if (offerInfo) {
                if (resetRepeatUnlocked) {
                    offerInfo.repeatUnlocked = false;
                }
                if (resetRevealed) {
                    offerInfo.revealed = false;
                    resetRedeemed = true;
                    resetBounceback = true;
                }
                if (resetRedeemed) {
                    offerInfo.redeemedDate = null;
                    offerInfo.nearbyDate = null;
                    resetBounceback = true;
                }
                if (resetBounceback) {
                    resetThumbsup = true;
                    if (offerInfo.redeemedDate) {
                        if (offerInfo.bounceBackOfferId) {
                            const when: Date = this.settings.calculateBounceBack(this.settings.calculateCurrentTime(), offerInfo.sample);
                            offerInfo.bouncebackDate = toDateStr(when);
                        }
                    } else {
                        offerInfo.bouncebackDate = null;
                    }
                }
                if (resetThumbsup) {
                    offerInfo.thumbsDate = null;
                    offerInfo.thumbs = null;
                }
                if (resetVideo) {
                    offerInfo.watchedVideo = null;
                }
            }
            return offerInfo;
        };

        return new Promise((resolve, reject) => {
            this.rpcService.OfferReset({ offer_id: offerId, resetRedeemed, resetRevealed, resetRepeatUnlocked, resetThumbsup, resetBounceback, resetVideo }).then((offerResult) => {
                console.log("RESET!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!!");
                console.log(offerResult);
                const offer: OfferInfo = this.readAndStoreOfferInfoRPC(offerResult);
                resolve(offer);
            }, (error) => {
                reject(error);
            });
        });
    }

    recordOfferExperience(offerId: number, thumbs: "up"|"down"|"skip"): SuccessResult {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            const userInfo: UserInfo = this.userProfile.userInfo;
            if (userInfo && userInfo.authToken) {
                if (thumbs === "up") {
                    this.rpcService.OfferThumbsUp_queue({ offer_id: offerId });
                    offerInfo.thumbs = thumbs;
                    offerInfo.thumbsDate = toDateStr(this.settings.calculateCurrentTime());
                    this.storageService.storeOfferInfo(offerInfo);
                    return { success: true };
                } else if (thumbs === "down") {
                    this.rpcService.OfferThumbsDown_queue({ offer_id: offerId });
                    offerInfo.thumbs = thumbs;
                    offerInfo.thumbsDate = toDateStr(this.settings.calculateCurrentTime());
                    this.storageService.storeOfferInfo(offerInfo);
                    return { success: true };
                } else {
                    this.rpcService.OfferThumbsSkip_queue({ offer_id: offerId });
                    offerInfo.thumbs = thumbs;
                    offerInfo.thumbsDate = toDateStr(this.settings.calculateCurrentTime());
                    this.storageService.storeOfferInfo(offerInfo);
                    return { success: true };
                }
            } else {
                return { success: false };
            }
        } else {
            return { success: false };
        }
    }

    recordOfferAdditionalPurchase(offerId: number, yesNo: "yes"|"no"): SuccessResult {
        const offerInfo = this.storageService.loadOfferInfo(offerId);
        if (offerInfo) {
            const userInfo: UserInfo = this.userProfile.userInfo;
            if (userInfo && userInfo.authToken) {
                if (yesNo === "yes") {
                    this.rpcService.OfferAdditionalPurchase_queue({ offer_id: offerId });
                    offerInfo.purchaseSurveyAnswer = yesNo;
                    offerInfo.purchaseSurveyDate = toDateStr(this.settings.calculateCurrentTime());
                    this.storageService.storeOfferInfo(offerInfo);
                    return { success: true };
                } else {
                    this.rpcService.OfferNoAdditionalPurchase_queue({ offer_id: offerId });
                    offerInfo.purchaseSurveyAnswer = yesNo;
                    offerInfo.purchaseSurveyDate = toDateStr(this.settings.calculateCurrentTime());
                    this.storageService.storeOfferInfo(offerInfo);
                    return { success: true };
                }
            } else {
                return { success: false };
            }
        } else {
            return { success: false };
        }
    }

    retrieveOffer(offerID: number): Promise<OfferInfoList> {
      return this.rpcService.OfferSearch({offer_id: offerID});
    }

    performSearch(): Promise<Array<OfferInfo>> {
        // clear out last search
        const offerList: Array<number> = [];
        this.storageService.storeOfferListSearch([]);

        // perform new search
        return new Promise((resolve, reject) => {
            const offers: Array<OfferInfo> = [];
            this.queryOffers(this.storageService.loadOfferSearch()).then((data: OfferInfoList) => {
                const currentZip: string = data.zip;
                const resultArea: OfferSearchResultLocation = {
                    latitude: data.latitude,
                    longitude: data.longitude,
                    radius: data.radius
                };
                if (data.expire) {
                    this.storageService.storeOfferSearchExpiresDate(toDate(data.expire));
                }
                this.storageService.storeOfferSearchResultLocation(resultArea);
                if (currentZip) {
                    try {
                        // this.notificationService.setNotificationTopic("zip" + Number(currentZip));
                    } catch (error) {
                        Log.error(Log.Tag.OfferService, error);
                    }
                }
                const adInfo: AdInfoRPC = data.ad_info;
                let sponsorInfo: SponsorInfo = null;
                if (adInfo) {
                    sponsorInfo = new SponsorInfo();
                    sponsorInfo.id = null;
                    sponsorInfo.name = adInfo.sponsor_name;
                    sponsorInfo.description = adInfo.sponsor_description;
                    sponsorInfo.destinationUrl = HttpOfferService.extractHref(adInfo.sponsor_url);
                    sponsorInfo.logoImageUrl = adInfo.sponsor_image_url;
                    sponsorInfo.logoImageHash = adInfo.sponsor_image_hash;
                    sponsorInfo.videoUrl = adInfo.ad_video_url;
                    sponsorInfo.videoHash = adInfo.ad_video_hash;
                    sponsorInfo.companyPhone = adInfo.sponsor_phone;
                    sponsorInfo.addressStreet = adInfo.sponsor_address_line1;
                    sponsorInfo.addressCity = adInfo.sponsor_city;
                    sponsorInfo.addressState = adInfo.sponsor_state;
                    sponsorInfo.addressZip = adInfo.sponsor_zip;
                    const address = parseAddress(adInfo.sponsor_address);
                    if (address) {
                        sponsorInfo.addressStreet = address.street1;
                        sponsorInfo.addressCity = address.city;
                        sponsorInfo.addressState = address.state;
                        sponsorInfo.addressZip = address.zip;
                    }
                    sponsorInfo.longitude = adInfo.sponsor_longitude;
                    sponsorInfo.latitude = adInfo.sponsor_latitude;
                    sponsorInfo.showThankYou = !adInfo.sponsor_disable_thankyou;
                    sponsorInfo.showSponsorPage = !adInfo.sponsor_disable_page;

                    const ad: AdInfo = new AdInfo();
                    ad.id = adInfo.ad_id;
                    ad.name = adInfo.ad_name;
                    ad.description = adInfo.ad_description;
                    ad.destinationUrl = HttpOfferService.extractHref(adInfo.ad_url);
                    ad.adImageUrl = adInfo.ad_image_url;
                    ad.adImageHash = adInfo.ad_image_hash;
                    sponsorInfo.ad = ad;
                    this.storageService.storeSponsorInfo(sponsorInfo);
                } else {
                    this.storageService.storeSponsorInfo(null);
                }

                const list: Array<OfferInfoRPC> = data.offers;
                Log.trace(Log.Tag.HttpOfferService, "Search result data");
                Log.trace(Log.Tag.HttpOfferService, data);
                if (list) {
                    list.forEach((item: OfferInfoRPC) => {
                        const processOfferData = (offerData: OfferInfoRPC) => {
                            const offer: OfferInfo = this.readAndStoreOfferInfoRPC(offerData);
                            offerList.push(offer.id);
                            offers.push(offer);
                        };
                        processOfferData(item);
                    });
                }
                Log.trace(Log.Tag.HttpOfferService, "Parsed Offers");
                Log.trace(Log.Tag.HttpOfferService, offers);

                this.storageService.storeOfferListSearch(offerList);
                resolve(offers);
            }, (error) => {
                reject(error);
            });
        });
    }

    alternateLocations(search: OfferSearch, limit: number): Promise<AlternateLocationsList> {
        if (!search) {
            search = this.storageService.loadOfferSearch();
        }
        return new Promise((resolve, reject) => {
            const defaultResult = { locations: [{ label: "Los Angeles, CA", zip: "92805" }]};
            if (search.location || !search.zipCode) {
                this.locationService.getLocationInfo().then((location) => {
                    if (location) {
                        this.rpcService.AlternateLocations({ latitude: location.latitude, longitude: location.longitude, limit: limit }).then((result) => {
                            resolve(result);
                        }, (error) => {
                            resolve(defaultResult);
                        });
                    } else {
                        this.rpcService.AlternateLocations({ limit: limit }).then((result) => {
                            resolve(result);
                        }, (error) => {
                            resolve(defaultResult);
                        });
                    }
                }, (error) => {
                    resolve(defaultResult);
                });
            } else if (search.zipCode) {
                this.rpcService.AlternateLocations({ zip: search.zipCode, limit: limit }).then((result) => {
                    resolve(result);
                }, (error) => {
                    resolve(defaultResult);
                });
            }
        });
    }

    loadFilterOptions(): OfferFilterOptions {
        const options: OfferFilterOptions = {};
        options.categories = [];
        options.categoryTagSections = new Map();
        const offerList: Array<number> = this.storageService.loadOffersListSearch();
        offerList.forEach((id) => {
            const info = this.storageService.loadOfferInfo(id);
            // console.log(info.category);
            if (info.category) {
                for (const c of info.category) {
                    if (options.categories.indexOf(c) < 0) {
                        options.categories.push(c);
                    }
                    if (info.tags) {
                        for (const t of info.tags) {
                            // add new tagsection if first for this category
                            if (!options.categoryTagSections.has(c)) options.categoryTagSections.set(c, new Map());
                            const tagSection: Map<string, Array<string>> = options.categoryTagSections.get(c);
                            let section = t.section;
                            if (!section) section = c;
                            if (!tagSection.has(section)) tagSection.set(section, []);
                            const tags: Array<string> = tagSection.get(section);
                            if (tags.indexOf(t.tag) < 0) tags.push(t.tag);
                        }
                    }
                }
            }
        });
        return options;
    }

    private queryOffers(search: OfferSearch): Promise<OfferInfoList> {
        return new Promise((resolve, reject) => {
            const userInfo: UserInfo = this.userProfile.userInfo;
            if (!search) {
                return null;
            }
            if (search.location) {
                this.locationService.getLocationInfo().then((location) => {
                    if (location) {
                        return this.rpcService.OfferSearch({ latitude: location.latitude, longitude: location.longitude, radius: search.radius, daysBack: 7 }).then((result) => {
                            resolve(result);
                        }, (error) => {
                            reject(error);
                        });
                    } else {
                        resolve({ expire: null, longitude: null, latitude: null, radius: null, zip: null, offers: [], ad_info: null });
                    }
                }, (error) => {
                    reject(error);
                });
            } else if (search.zipCode) {
              let zipCode = search.zipCode;
              while (zipCode.length < 5) {
                zipCode = '0' + zipCode;
              }
                return this.rpcService.OfferSearch({ zip: search.zipCode, radius: search.radius, daysBack: 7  }).then((result) => {
                    resolve(result);
                }, (error) => {
                    reject(error);
                });
            }
        });
    }

    private readAndStoreOfferInfoRPC(offerData: OfferInfoRPC): OfferInfo {
        const offer: OfferInfo = HttpOfferService.convertOfferInfoRPC(offerData, this.settings);

        const existingOffer = this.storageService.loadOfferInfo(offer.id);
        if (existingOffer) offer.seenDate = existingOffer.seenDate;
        else {
            const newSeenDate = this.settings.calculateCurrentTime();
            // let offer show as "new" for one week by detault
            newSeenDate.setTime(newSeenDate.getTime() + (7 * 24 * 60 * 60 * 1000));
            offer.seenDate = toDateStr(newSeenDate);
        }

        this.storageService.storeOfferInfo(offer);

        if (offer.redeemedDate) {
            this.storageService.addOffersListRedeemedOffer(offer.id);
        }
        return offer;
    }

    private static extractHref(link: string) {
        if (!link) {
            return null;
        }
        if (link.startsWith("<a href=")) {
            try {
                const firstQuote = link.indexOf("\"");
                const secondQuote = link.indexOf("\"", firstQuote + 1);
                const href = link.substring(firstQuote + 1, secondQuote);
                return href;
            } catch (error) {
                Log.debug(Log.Tag.HttpOfferService, error);
                return link;
            }
        } else {
            return link;
        }
    }

    public static convertOfferInfoRPC(offerData: OfferInfoRPC, settings: Settings): OfferInfo {
        const offer: OfferInfo = new OfferInfo();
        offer.id = offerData.offer_id;
        offer.sortPriority = offerData.sort_priority;
        offer.free = offerData.is_free;
        offer.bounceback = offerData.is_bounceback;

        offer.revealed = offerData.revealed;
        offer.revealCost = offerData.reveal_cost;
        offer.favorite = offerData.favorite;
        offer.favoriteCost = settings.favoriteCost;
        offer.repeatUnlocked = offerData.repeat_unlocked;
        offer.repeatUnlockCost = offerData.repeat_unlock_cost;

        offer.sample = offerData.is_sample;
        offer.bounceBackOfferId = offerData.bounceback_to_offer_id && offerData.bounceback_to_offer_id > 0 ? offerData.bounceback_to_offer_id : null;
        offer.startDate = toDateStr(toMoment(offerData.start_date, true));
        offer.name = offerData.offer_name;
        offer.terms = offerData.offer_description;
        // offerData.offer_additional_details = "This offer is also only available between 5 and 6pm on Tuesdays. Only valid if there was a full moon the night before";
        offer.category = offerData.filter_category;
        offer.tags = offerData.filter_tags;
        offer.additionalTerms = offerData.offer_additional_details;
        offer.whatElseDescription = offerData.what_else_description;
        offer.weeklyHours = offerData.weekly_hours;
        offer.expirationDate = toDateStr(toMoment(offerData.end_date, true));
        offer.nextAvailableDate = toDateStr(settings.setTimeOfDay(offerData.next_available_date, 10));
        offer.imageUrl = offerData.offer_image_url;
        offer.imageHash = offerData.offer_image_hash;
        offer.companyId = offerData.organization_id;
        offer.companyName = offerData.organization_name;
        // offerData.organization_description = "Here is some additional descriptive information about the company. It's a great company and has been around for a really long time.\n\nWhen you think of fun, think of us!";
        offer.companyDescription = offerData.organization_description;
        offer.companyLogoUrl = offerData.organization_image_url;
        offer.companyLogoHash = offerData.organization_image_hash;
        offer.companyWebUrl = HttpOfferService.extractHref(offerData.organization_url);
        offer.addresses = [];
        // offerData.organization_addresses = [
        //     { label: "Springville", address: "2762 E 1100 S, Springville, UT, 84664", latitude: 40.148489, longitude: -111.5600686, phone: "+18014915002" },
        //     { label: "Provo", address: "2777 W 370 N, Provo, UT, 84601", latitude: 40.2383843, longitude: -111.7076533, phone: "+18014915001" }
        // ];
        if (offerData.organization_addresses) {
            offerData.organization_addresses.forEach((addr) => {
                const address: AddressInfo = {
                    addressId: addr.address_id,
                    label: addr.label,
                    street1: addr.address_line1,
                    street2: addr.address_line2,
                    city: addr.city,
                    state: addr.state,
                    zip: addr.zip,
                    phone: addr.phone,
                    latitude: addr.lat,
                    longitude: addr.lng
                };
                if (addr.address) {
                    const parsed = parseAddress(addr.address);
                    if (parsed) {
                        address.street1 = parsed.street1;
                        address.street2 = parsed.street2;
                        address.city = parsed.city;
                        address.state = parsed.state;
                        address.zip = parsed.zip;
                    }
                }
                offer.addresses.push(address);
            });
        } else {
            const address: AddressInfo = {
                street1: offerData.organization_address_line1,
                city: offerData.organization_city,
                state: offerData.organization_state,
                zip: offerData.organization_zip,
                phone: offerData.organization_phone,
                latitude: offerData.organization_latitude,
                longitude: offerData.organization_longitude
            };
            const parsed = parseAddress(offerData.organization_address);
            if (parsed) {
                address.street1 = parsed.street1;
                address.street2 = parsed.street2;
                address.city = parsed.city;
                address.state = parsed.state;
                address.zip = parsed.zip;
            }
            offer.addresses.push(address);
        }

        offer.onlineOnly = !!offerData.online_only;
        offer.shipping = offerData.shipping;
        offer.onlineRedeemUrl = offerData.online_redeem_url;
        // // TODO remove the follow test block
        // if (offerData.offer_name.indexOf("Day") > 0) {
        //     offer.onlineOnly = true;
        //     offer.onlineRedeemUrl = offer.companyWebUrl;
        //     offer.addresses = [];
        // }

        offer.redeemedDate = toDateStr(offerData.redeemed_timestamp, true);
        offer.watchedVideo = offerData.viewed ? toDateStr(new Date(), true) : null;
        offer.thumbsDate = toDateStr(offerData.rating_timestamp, true);
        offer.purchaseSurveyDate = toDateStr(offerData.redeemed_timestamp, true);
        offer.reviewUrl = offerData.review_url;

        offer.bouncebackDate = toDateStr(settings.calculateBounceBack(toDate(offer.redeemedDate), offer.sample));

        if (offer.category === null) offer.category = [];
        return offer;
    }
}
